// Third-party libraries
import React, { Fragment } from "react";
import { Link } from "react-router-dom";

import { Meta } from "../utilities";

function Rules() {
  return (
    <Fragment>
      <Meta
        title="Rouleur Derby Rules!"
        description="The rules for Rouleur Derby, almost certainly the best fantasy cycling game in the world."
      />
      <h1>Rouleur Derby Rules</h1>

      <h4>Rules About Betting</h4>
      <ol className="rules-list">
        <li>
          Betting closes at midnight the evening before the race. For bets
          involving stage races, this is midnight the evening before the first
          stage. (All times are Chicago time.)
        </li>
        <li>Players may not cancel bets.</li>
        <li>Bets must be in whole numbers.</li>
        <li>
          A player’s total bets in any given proposition may not exceed 50
          points, or 5 percent of the proposition’s action, whichever is more.
        </li>
        <li>
          If a rider withdraws before the start of the race, for any reason,
          bets on that rider are canceled and refunds are issued. This is known
          as a “scratch.”
        </li>
      </ol>
      <h4>Rules about Odds and Outcomes</h4>
      <ol className="rules-list">
        <li>
          The house furnishes a list of possible outcomes for each race as soon
          as reliable start lists are available. Many races will also have the
          option of “Other,” an instrument with which one can bet upon any rider
          not on the house’s list of favorites.
        </li>
        <li>
          The house provides preliminary odds—a “morning line”—for all of each
          proposition’s possible outcomes. As bets are made, those bets will
          gradually change, with increasing frequency as action increases and as
          the betting deadline approaches.
        </li>
        <li>Odds may not always add up to exactly 100% probability.</li>
      </ol>

      <h4>Rules About Scoring</h4>
      <ol className="rules-list">
        <li>
          Bets will be validated and paid out as soon as prudent and possible
          after each race.{" "}
        </li>
        <li>
          Payouts are rounded to the nearest whole number. Payouts of less than
          a point will be rounded up to one.
        </li>

        <li>
          Should riders be relegated after bets have been validated—such as if a
          winning rider is expelled for doping—the payouts will not be revised.
          However, if a winning rider is immediately relegated for, say, an
          inappropriate sprint, Rouleur Derby will recognize the official winner
          of the race, not the rider who was relegated.
        </li>
        <li>
          Occasionally Rouleur Derby will err and score the incorrect outcome of
          a proposition. In such events, payouts will be reversed and diverted
          to the rightful winners.
        </li>
        <li>
          If “Other” is included as an option in a show proposition, “Other”
          will pay out no more than once, even if the podium includes more than
          one rider matching that criterion.
        </li>
        <li>
          Picking the winner of any of the following races will earn a 25
          percent bonus: Milan-San Remo, Tour of Flanders, Paris-Roubaix,
          Liège-Bastogne-Liège, and the World Championships. In other words, a
          winning bet that would ordinarily pay a profit of 100 points would
          instead pay 125 points. Picking the winner of the grand tours—Giro
          d'Italia, Tour de France, or the Vuelta a España—will also earn a 25
          percent bonus.
        </li>

        <li>
          Members of{" "}
          <a
            href="https://www.wideanglepodium.com"
            rel="noopener noreferrer"
            target="_blank"
          >
            Wide Angle Podium
          </a>{" "}
          receive 500-point bonuses.
        </li>
        <li>
          Players can earn up to 300 points by inviting players to join using
          the secret link provided on one’s profile page.
        </li>
        <li>
          Players who run out of points may request a bailout loan no more than
          once every seven days. Accounts will be replenished, but the player
          will spend three days on probation, during which they may not bet more
          than 100 points within a single proposition. Players with outstanding
          loans will have asterisks appear next to their name in the standings.
          Loans—and the 10 percent vigorish they are charged—can be paid back at
          any time.
        </li>
        <li>
          In addition to the <Link to="/rd/leaders">overall standings</Link>,
          there are rankings for “contests,” including for the Spring Classics
          and the Grand Tours. To be ranked within contests, players must make a
          minimum number of bets and risk a minimum number of points.
        </li>
        <li>
          Results in stage races refer to the final general classification,
          unless explicitly noted as pertaining to a specific stage or other
          outcome.
        </li>
        <li>
          In head-to-head match-ups, both riders must start, and at least one
          must finish, unless noted otherwise. Failing those conditions, bets
          will be voided and points refunded.
        </li>
      </ol>
      <h4>Rules About Propositions</h4>
      <ol className="rules-list">
        <li>
          Unless a proposition explicitly refers to women, it is the men’s race
          that is in question.
        </li>
        <li>
          For any proposition that refers to the podium, “podium” is defined as
          first, second, or third.
        </li>
        <li>
          Unless a proposition says otherwise, “Which team will win …” refers to
          the team of the rider who wins the race, not the team classification.
        </li>
        <li>
          If a proposition asks for a number—such as, "How many stages of the
          Tour de France will Peter Sagan win?"—the options mean exactly that
          many, unless otherwise noted. “Two,” therefore, means “exactly two.”
        </li>

        <li>
          Riders will never be added to a proposition after bets have been made.
          Should there be a late addition to a start list, or should a favorite
          be left out of the morning line, that rider will be considered to be
          included in “Other.”
        </li>
      </ol>
      <h4>Other Rules</h4>
      <ol className="rules-list">
        <li>
          Suspicious betting patterns (such as creating puppet accounts or
          colluding with other players) will not be tolerated. Penalties may
          include public shaming or banishment.
        </li>
        <li>
          Participation in the{" "}
          <a
            href="http://rouleurderby.slack.com"
            rel="noopener noreferrer"
            target="_blank"
          >
            Rouleur Derby Slack community
          </a>{" "}
          is a privilege and can be revoked at any time for any reason,
          including but not limited to harrassment, inappropriate language, or
          general boorishness. Don’t be a jerk.
        </li>
        <li>
          You must be 18 years or older to play Rouleur Derby. If you or someone
          you know has a gambling problem and wants help, call 1-800 GAMBLER.
        </li>
      </ol>

      <p>Good luck!</p>
    </Fragment>
  );
}

export default Rules;
